import { format } from "date-fns";
import { Trash2, Zap } from "lucide-react";

import {
  useBoardFeatureDetail,
  useBoardFeatures,
  useComponentDependencies,
  useDeleteBoardFeature,
  useFeatureActivity,
  useMilestones,
  useMvpComponents,
  useUpdateBoardFeature,
  useWorkstreams,
} from "@/application/use-cases/board/use-board.queries";
import { useStakeholdersList } from "@/application/use-cases/stakeholders/use-stakeholders.queries";
import { BoardActivityFeed } from "@/presentation/components/board/board-activity-feed";
import { BoardReviewTicketSection } from "@/presentation/components/board/board-review-ticket-section";
import { DependencyPicker } from "@/presentation/components/board/dependency-picker";
import { StatusSelect } from "@/presentation/components/board/status-select";
import { DomainPill } from "@/presentation/components/roadmaps/domain-pill";
import { IMPL_KIND_LABEL, STAGE_LABEL } from "@/presentation/data/board";
import { Button } from "@monorepo-apps/ui/src/components/button";
import { Label } from "@monorepo-apps/ui/src/components/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@monorepo-apps/ui/src/components/select";
import { Textarea } from "@monorepo-apps/ui/src/components/textarea";
import type {
  BoardFeatureRow,
  BoardImplKind,
  MvpComponentRow,
} from "@/infrastructure/supabase/database.types";

const NONE = "__none__";
const IMPL_KINDS: BoardImplKind[] = ["native", "build", "scratch", "mixed"];

export function BoardFeatureDrawerContent({
  id,
  onClose,
  readOnly = false,
}: {
  id: string;
  onClose: () => void;
  readOnly?: boolean;
}) {
  const featureQ = useBoardFeatureDetail(id);
  const componentsQ = useMvpComponents();
  const workstreamsQ = useWorkstreams();
  // Cached by the board list query — used only to place a moved item at the end
  // of its destination block.
  const allFeaturesQ = useBoardFeatures();
  const milestonesQ = useMilestones();
  const stakeholdersQ = useStakeholdersList();
  const depsQ = useComponentDependencies();
  const activityQ = useFeatureActivity(id);
  const update = useUpdateBoardFeature();
  const remove = useDeleteBoardFeature();

  if (!featureQ.data) {
    return <p className="text-muted-foreground px-6 py-8 text-xs italic">Loading…</p>;
  }

  const feature = featureQ.data;
  const components = componentsQ.data ?? [];
  const milestones = milestonesQ.data ?? [];
  const stakeholders = stakeholdersQ.data ?? [];
  const dependencies = depsQ.data ?? [];

  const component = components.find((c) => c.id === feature.component_id);
  const workstream = (workstreamsQ.data ?? []).find((w) => w.key === feature.workstream_key);
  const componentsById = new Map(components.map((c) => [c.id, c]));
  const deps = component
    ? dependencies
        .filter((d) => d.component_id === component.id)
        .map((d) => componentsById.get(d.depends_on_component_id))
        .filter((c): c is MvpComponentRow => Boolean(c))
    : [];

  const workstreamCodeByKey = new Map((workstreamsQ.data ?? []).map((w) => [w.key, w.code]));

  const activityLookups = {
    stakeholdersById: new Map(stakeholders.map((s) => [s.id, s.name])),
    componentsById,
    milestonesByKey: new Map(milestones.map((m) => [m.key, m])),
    workstreamsByKey: new Map((workstreamsQ.data ?? []).map((w) => [w.key, w])),
  };

  const patch = (p: Partial<BoardFeatureRow>) => {
    if (readOnly) return;
    // Same rule as the board's Block column: a block change lands the item at the
    // end of the destination rather than keeping its old block's sort_order.
    if (p.workstream_key !== undefined && p.sort_order === undefined) {
      const target = p.workstream_key;
      const last = (allFeaturesQ.data ?? []).reduce(
        (max, f) =>
          f.id !== feature.id && f.workstream_key === target ? Math.max(max, f.sort_order) : max,
        0
      );
      p = { ...p, sort_order: last + 1 };
    }
    update.mutate({ id: feature.id, patch: p });
  };

  return (
    <div className="flex h-full flex-col overflow-hidden">
      <header className="border-border/60 flex flex-col gap-2 border-b px-6 py-5">
        <div className="flex flex-wrap items-center gap-2">
          <span className="eyebrow">
            {workstream ? `Block ${workstream.code} · ${workstream.title}` : feature.workstream_key}
          </span>
          {feature.mvp_blocking ? (
            <span className="font-data inline-flex items-center gap-1 text-[10px] tracking-[0.12em] text-[var(--rag-red)] uppercase">
              <Zap className="size-3" /> Critical path
            </span>
          ) : null}
        </div>
        <h2 className="text-[17px] leading-snug font-semibold tracking-tight">{feature.title}</h2>
        {component ? (
          <div className="flex flex-wrap items-center gap-2">
            <span className="font-data text-foreground text-[12px]">{component.name_en}</span>
            {component.name_he ? (
              <span className="text-muted-foreground text-[12px]" dir="rtl">
                {component.name_he}
              </span>
            ) : null}
            <span className="text-muted-foreground">·</span>
            <span className="text-muted-foreground text-[12px]">
              {STAGE_LABEL[component.stage]}
            </span>
            {component.domain ? <DomainPill domain={component.domain} /> : null}
          </div>
        ) : null}
      </header>

      <div className="flex-1 overflow-y-auto px-6 py-5">
        <div className="flex flex-col gap-5">
          {feature.description ? (
            <p className="text-foreground text-[13.5px] leading-relaxed">{feature.description}</p>
          ) : null}

          {feature.acceptance_md ? (
            <div className="flex flex-col gap-1.5 rounded-md border-l-2 border-[var(--rag-green)] bg-[oklch(0.66_0.16_155_/_0.06)] px-3.5 py-3">
              <span className="eyebrow text-[var(--rag-green)]">Acceptance</span>
              <p className="text-foreground text-[13px] leading-snug">{feature.acceptance_md}</p>
            </div>
          ) : null}

          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <Field label="Block">
              <Select
                value={feature.workstream_key}
                onValueChange={(v) => {
                  if (v !== feature.workstream_key) patch({ workstream_key: v });
                }}
                disabled={readOnly}
              >
                <SelectTrigger className="h-9 w-full text-[13px]">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {(workstreamsQ.data ?? []).map((w) => (
                    <SelectItem key={w.key} value={w.key} className="text-[13px]">
                      Block {w.code} — {w.title}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </Field>

            <Field label="Delivery status">
              <StatusSelect
                value={feature.delivery_status}
                onChange={(v) => patch({ delivery_status: v })}
                disabled={readOnly}
              />
            </Field>

            <Field label="Milestone">
              <Select
                value={feature.milestone_key ?? NONE}
                onValueChange={(v) => patch({ milestone_key: v === NONE ? null : v })}
                disabled={readOnly}
              >
                <SelectTrigger className="h-9 w-full text-[13px]">
                  <SelectValue placeholder="None" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value={NONE} className="text-[13px]">
                    None
                  </SelectItem>
                  {milestones.map((m) => (
                    <SelectItem key={m.key} value={m.key} className="text-[13px]">
                      {m.code} — {m.title}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </Field>

            <Field label="Implementation">
              <Select
                value={feature.impl_kind ?? NONE}
                onValueChange={(v) =>
                  patch({ impl_kind: v === NONE ? null : (v as BoardImplKind) })
                }
                disabled={readOnly}
              >
                <SelectTrigger className="h-9 w-full text-[13px]">
                  <SelectValue placeholder="—" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value={NONE} className="text-[13px]">
                    —
                  </SelectItem>
                  {IMPL_KINDS.map((k) => (
                    <SelectItem key={k} value={k} className="text-[13px]">
                      {IMPL_KIND_LABEL[k]}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </Field>

            <Field label="Owner">
              <Select
                value={feature.owner_id ?? NONE}
                onValueChange={(v) => patch({ owner_id: v === NONE ? null : v })}
                disabled={readOnly}
              >
                <SelectTrigger className="h-9 w-full text-[13px]">
                  <SelectValue placeholder="Unassigned" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value={NONE} className="text-[13px]">
                    Unassigned
                  </SelectItem>
                  {stakeholders.map((s) => (
                    <SelectItem key={s.id} value={s.id} className="text-[13px]">
                      {s.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </Field>
          </div>

          <Field label="Depends on (prerequisites)">
            <DependencyPicker
              variant="inline"
              feature={feature}
              componentsById={componentsById}
              workstreamCodeByKey={workstreamCodeByKey}
              readOnly={readOnly}
            />
          </Field>

          {deps.length > 0 ? (
            <div className="flex flex-col gap-1.5">
              <span className="eyebrow">Component graph (reference)</span>
              <div className="flex flex-wrap gap-1.5">
                {deps.map((d) => (
                  <span
                    key={d.id}
                    className="font-data border-border text-muted-foreground rounded border px-2 py-0.5 text-[11px]"
                  >
                    {d.name_en}
                  </span>
                ))}
              </div>
            </div>
          ) : null}

          <Field label="Notes">
            <Textarea
              key={feature.id}
              defaultValue={feature.notes ?? ""}
              onBlur={(e) => {
                const next = e.target.value;
                if (next !== (feature.notes ?? "")) patch({ notes: next || null });
              }}
              placeholder="Implementation notes, decisions, blockers…"
              className="min-h-20 text-[13px]"
              disabled={readOnly}
            />
          </Field>

          <label className="text-foreground flex w-fit cursor-pointer items-center gap-2 text-[13px]">
            <input
              type="checkbox"
              checked={feature.mvp_blocking}
              onChange={(e) => patch({ mvp_blocking: e.target.checked })}
              disabled={readOnly}
              className="size-3.5 accent-[var(--rag-red)]"
            />
            Flag as MVP-blocking / critical path
          </label>

          <div className="flex flex-col gap-1.5">
            <span className="eyebrow">Activity</span>
            <BoardActivityFeed
              entries={(activityQ.data?.pages ?? []).flatMap((p) => p.rows)}
              isLoading={activityQ.isLoading}
              lookups={activityLookups}
              hasMore={activityQ.hasNextPage}
              isLoadingMore={activityQ.isFetchingNextPage}
              onLoadMore={() => activityQ.fetchNextPage()}
            />
          </div>

          {/* block-v items ARE generated review-ticket validation items — surface
              the ticket state + editable flow content here (populated only for
              block-v; see BoardReviewTicketSection). */}
          {feature.workstream_key === "block-v" ? (
            <BoardReviewTicketSection
              boardCode={feature.code}
              boardTitle={feature.title}
              readOnly={readOnly}
            />
          ) : null}

          <div className="border-border/40 flex items-center justify-between border-t pt-3">
            <span className="font-data text-muted-foreground text-[10px] tracking-wider">
              Updated {format(new Date(feature.updated_at), "d MMM, HH:mm")}
            </span>
            {readOnly ? null : (
              <Button
                type="button"
                variant="ghost"
                size="sm"
                className="gap-1.5 text-[var(--rag-red)] hover:text-[var(--rag-red)]"
                onClick={() => {
                  remove.mutate(feature.id);
                  onClose();
                }}
              >
                <Trash2 className="size-3.5" /> Delete
              </Button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="flex flex-col gap-1.5">
      <Label className="eyebrow">{label}</Label>
      {children}
    </div>
  );
}
